import React, { useState, useEffect, useContext } from "react";
import { Button, Tabs, Row, Col, Collapse } from "antd";
import { useNavigate } from "react-router-dom";
import Questions from '../questions/questions.component';
import { listFaqs } from '../../services/faq.service';
import FaqCode from "../../enums/faqCode.enum";
import "./treatments.scss";
import { RightCircleFilled } from "@ant-design/icons";
import FlowType from "../../enums/flowType.enum";
import CitationFootnote from "../citationFootnote/citationFootnote.component";
import TreatmentTimeline from "./treatmentTimeline.component";
import PosthogHelper from "../../helpers/posthog.helper";
import { UserContext } from "../../contexts/user.context";

const { Panel } = Collapse;


const citations = [
  {
    index: 1,
    authors: "C. Glueck et al.",
    title: "Rosuvastatin 5 and 10 mg/d: a pilot study of the effects in hypercholesterolemic adults unable to tolerate other statins and reach LDL cholesterol goals with nonstatin lipid-lowering therapies.",
    journal: "Clinical therapeutics (2006)",
    link: "https://doi.org/10.1016/J.CLINTHERA.2006.06.004"
  },
  {
    index: 2,
    authors: "Luke J. Laffin et al.",
    title: "Comparative Effects of Low-Dose Rosuvastatin, Placebo and Dietary Supplements on Lipids and Inflammatory Biomarkers.",
    journal: "Journal of the American College of Cardiology (2022)",
    link: "https://doi.org/10.1016/j.jacc.2022.10.013"
  },
  {
    index: 3,
    authors: "Y. Saito et al.",
    title: "Effect of rosuvastatin 5-20mg on triglycerides and other lipid parameters in Japanese patients with hypertriglyceridemia.",
    journal: "Atherosclerosis (2007)",
    link: "https://doi.org/10.1016/J.ATHEROSCLEROSIS.2006.11.028"
  },
  {
    index: 4,
    authors: "Durrington, P., Tuomilehto, J., Hamann, A., Kallend, D., & Smith, K.",
    title: "Rosuvastatin and fenofibrate alone and in combination in type 2 diabetes patients with combined hyperlipidaemia.",
    journal: "Diabetes research and clinical practice (2004)",
    link: "https://doi.org/10.1016/J.DIABRES.2003.11.012"
  },
  {
    index: 5,
    authors: "Bando, Y., Toyama, H., Kanehara, H., Hisada, A., Okafuji, K., Toya, D., & Tanaka, N.",
    title: "Switching from atorvastatin to rosuvastatin lowers small, dense low-density lipoprotein cholesterol levels in Japanese hypercholesterolemic patients with type 2 diabetes mellitus.",
    journal: "Diabetes research and clinical practice (2014)",
    link: "https://doi.org/10.1016/j.diabres.2015.10.013"
  },
  {
    index: 6,
    authors: "K. Koh et al.",
    title: "Rosuvastatin dose-dependently improves flow-mediated dilation, but reduces adiponectin levels and insulin sensitivity in hypercholesterolemic patients.",
    journal: "International journal of cardiology (2016)",
    link: "https://doi.org/10.1016/j.ijcard.2016.08.051"
  },
  {
    index: 7,
    authors: "J. Bosch et al.",
    title: "Lowering cholesterol, blood pressure, or both to prevent cardiovascular events: results of 8.7 years of follow-up of Heart Outcomes Evaluation Prevention (HOPE)-3 study participants.",
    journal: "European Heart Journal (2021)",
    link: "https://doi.org/10.1093/eurheartj/ehab225"
  },
  {
    index: 8,
    authors: "S. Yusuf et al.",
    title: "Cholesterol Lowering in Intermediate-Risk Persons without Cardiovascular Disease.",
    journal: "The New England journal of medicine (2016)",
    link: "https://doi.org/10.1056/NEJMoa1600176"
  }


];

const mechanismData = [
  {
    key: "1",
    label: "🧬 Inhibits Cholesterol Production",
    content: (
      <>
        <p>
          <strong className="pathway-lead-in">Rosuvastatin blocks cholesterol at the source: your liver.</strong>
        </p>

        <p>
        Your liver is the main factory for cholesterol in your body. Rosuvastatin works by blocking a key enzyme in that factory called HMG-CoA reductase, which slows down how much cholesterol your liver can make. When cholesterol production goes down, your body builds fewer cholesterol-carrying particles that contribute to artery damage over time.
        </p>
        <p>
        These particles include LDL (low-density lipoprotein), as well as others like VLDL (very low-density lipoprotein) and Lp(a). What they all have in common is a structural protein called Apolipoprotein B (ApoB). You can think of ApoB as a tag that marks every particle capable of depositing cholesterol into artery walls. So when rosuvastatin reduces cholesterol production, it ultimately helps lower the number of ApoB-containing particles in your bloodstream.
        </p>

        <p>
        Unlike many statins, rosuvastatin is hydrophilic (water-soluble), which means it doesn't diffuse into other tissues like muscle or brain. Instead, it stays focused in the liver where it's needed most and is efficiently absorbed with the help of a specialized transporter called SLCO2B1. This ensures powerful cholesterol-lowering effects with fewer off-target side effects.
        </p>

  
      </>
    ),
  },
  {
    key: "2",
    label: "🧲 Increases LDL Receptors",
    content: (
      <>
        <p><strong className="pathway-lead-in">Rosuvastatin helps your liver pull harmful cholesterol-carrying particles out of circulation.</strong></p>
        <p>
        When rosuvastatin reduces cholesterol production in the liver, it triggers a feedback loop: liver cells respond by producing more LDL receptors—proteins on the surface of liver cells that bind to ApoB-containing particles (like LDL) and pull them out of the bloodstream.
        </p>
        <p>
        This mechanism is shared by all statins, but rosuvastatin produces a stronger LDL receptor response than most. That's because it suppresses cholesterol production more effectively, signaling the liver to ramp up receptor production more aggressively. On top of that, rosuvastatin is hydrophilic, meaning it stays concentrated in the liver unlike lipophilic statins, which disperse into other tissues. This liver selectivity focuses its action exactly where LDL receptors are expressed.
        </p>
        <p>
        Rosuvastatin also has a long half-life and undergoes minimal liver metabolism, which allows it to maintain steady, sustained activation of LDL receptors throughout the day. The result is greater clearance of ApoB-containing particles and significantly lower levels of LDL cholesterol in the blood even at low doses.
        </p>
        <p>
        This enhanced LDL receptor effect is one of the key reasons rosuvastatin outperforms other statins on both potency and consistency.
        </p>
 
      </>
    ),
  },
  {
    key: "3",
    label: "🔥 Reduces Inflammation",
    content: (
      <>
        <p><strong className="pathway-lead-in">Rosuvastatin doesn't just lower cholesterol; it also calms inflammation in your arteries.</strong></p>
        <p>
        Cardiovascular disease isn't just about cholesterol -- inflammation plays a central role in plaque buildup and rupture. When ApoB-containing particles like LDL slip into the walls of your arteries, they trigger an immune response. Over time, this leads to chronic inflammation, which weakens the artery wall, accelerates plaque growth, and increases the risk of heart attack and stroke.
        </p>
        <p>
        Rosuvastatin doesn't just lower the number of these particles—it also directly reduces inflammation. In clinical studies, rosuvastatin significantly lowers C-reactive protein (CRP), a key blood marker of vascular inflammation. This matters because people with high CRP levels are at elevated cardiovascular risk, even if their LDL cholesterol is normal.
        </p>
        <p>
        Compared to other statins, rosuvastatin has shown stronger and more consistent CRP-lowering effects, especially at low doses. This dual impact—on both cholesterol particles and inflammatory signaling—helps stabilize plaques, calm the blood vessels, and reduce the likelihood of sudden cardiovascular events.
        </p>
        <p>
        Rosuvastatin reduces this inflammation by both lowering particle burden and directly suppressing inflammatory markers like C-reactive protein (CRP), leading to more stable, resilient arteries over time.
        </p>

   
      </>
          
    ),
  },
  {
    key: "4",
    label: "🛡️ Slows / Reverses Plaque Buildup",
    content: (
     <>
     <p><strong className="pathway-lead-in">Rosuvastatin helps prevent and in some cases may reverse the buildup of plaque in your arteries.</strong></p>
     <p>
     Over time, excess ApoB-containing particles and inflammation lead to the formation of atherosclerotic plaque - a buildup of cholesterol, immune cells, and scar tissue inside the walls of your arteries. These plaques can restrict blood flow and, if unstable, rupture and trigger heart attacks or strokes.
     </p>
     <p>
     Rosuvastatin not only slows this process; it may even help reverse it. By reducing both the number of cholesterol-carrying particles (ApoB) and the inflammation that fuels plaque growth, rosuvastatin promotes a more stable vascular environment. Studies using imaging techniques like intravascular ultrasound have shown that rosuvastatin can reduce plaque volume over time, especially when LDL levels are brought very low.
     </p>
     <p>
     What makes rosuvastatin especially effective here is its potent, targeted action in the liver, which leads to deeper reductions in ApoB and LDL cholesterol, and its strong anti-inflammatory effects—even at low doses. Together, these benefits translate to slower plaque progression, improved artery function, and a reduced risk of cardiovascular events, even in people without prior heart disease.
     </p>
     <p>
     Think of it as turning down the fire (inflammation), reducing the fuel (ApoB), and strengthening the structure (plaque stability) all in one step.
     </p>

     </>
    ),
  },
];

const timelineItems = [
  {
    key: '1',
    label: 'Upload Lab Work',
    children: {
      label: 'STEP 1',
      title: 'Upload Lab Work',
      description: <>
        Confirm your eligibility for low-dose statin treatment with recent lab results and short health questionnaire.
      </>,
      img: '/img/timeline-baseline.jpg',
      body: <>
        <ul className="timeline-list">
          <li>
              <Row align="top">
                <Col>
                  <RightCircleFilled className="timeline-icon" />
                </Col>
                <Col>
                  Required: <a href="/test/apob" target="_blank">ApoB</a> or <a href="/test/direct-ldl" target="_blank">LDL cholesterol</a> within past 12 months
                </Col>
              </Row>
            </li>
            <li>
              <Row align="top">
                <Col>
                  <RightCircleFilled className="timeline-icon" />
                </Col>
                <Col>
                Quick and easy health questionnaire to ensure safe prescribing.
                </Col>
              </Row>
            </li>
            <li>
              <Row align="top">
                <Col>
                  <RightCircleFilled className="timeline-icon" />
                </Col>
                <Col>
                  Need labs? Get Instalab's <a href="/heart-health-test" target="_blank">Advanced Heart Health Test</a> for a complete picture.
                </Col>
              </Row>
            </li>
          </ul>
        </>
      }
  },
  {
    key: '2',
    label: `Start Treatment`,
    children: {
      label: 'STEP 2',
      title: 'Start Treatment',
      description: <>
        A board-certified lipidologist will personally review your lab results and health info to ensure rosuvastatin (5 mg) is the right fit for you.
      </>,
      img: '/img/treatments/medication-delivery.png',
      body: <>
  
          <ul className="timeline-list">
            <li>
              <Row align="top">
                <Col>
                  <RightCircleFilled className="timeline-icon" />
                </Col>
                <Col>
                  Reviewed and approved by a lipid specialist.
                </Col>
              </Row>
            </li>
            <li>
              <Row align="top">
                <Col>
                  <RightCircleFilled className="timeline-icon" />
                </Col>
                <Col>
                Medication shipped directly to your door.
                </Col>
              </Row>
            </li>
            <li>
              <Row align="top">
                <Col>
                  <RightCircleFilled className="timeline-icon" />
                </Col>
                <Col>
                  Concierge support available for any follow-up questions.
                </Col>
              </Row>
            </li>
          </ul>
        </>
      }
  }, 
  {
    key: '3',
    label: 'Track Progress',
    children: {
      label: 'STEP 3',
      title: 'Track Progress',
      description: <>
        See how your body is responding with a follow-up lab panel at 6–12 weeks.
      </>,
      img: '/img/timeline-chart.jpg',
      body: <>
          <ul className="timeline-list">
            <li>
              <Row align="top">
                <Col>
                  <RightCircleFilled className="timeline-icon" />
                </Col>
                <Col>
                  Look for a drop in ApoB and LDL cholesterol.
                </Col>
              </Row>
            </li>
            <li>
              <Row align="top">
                <Col>
                  <RightCircleFilled className="timeline-icon" />
                </Col>
                <Col>
                  Monitor liver (ALT) and blood sugar (HbA1c) for increases.
                </Col>
              </Row>
            </li>
            <li>
              <Row align="top">
                <Col>
                  <RightCircleFilled className="timeline-icon" />
                </Col>
                <Col>
                  Note any changes in energy, mood, or muscle comfort.
                </Col>
              </Row>
            </li>
          </ul>
          <p>Not seeing expected results? You can adjust your medication or consult a physician through our <a href="/heart">Heart Health Program</a>.</p>
        </>
      }
  }

]

const safetyInfo = [
    {
      key: "1",
      title: "Safe for Most at Low Dose",
      content: (<>
        <p>
        Rosuvastatin 5 mg is well tolerated by most people and is significantly less likely to cause side effects than higher doses commonly used in traditional practice. It’s a good starting point for individuals who are focused on lowering ApoB and LDL without taking on unnecessary medication intensity.
        </p>

        <p>Because this is a low-dose, long-term option, it does not require doctor visits to maintain your prescription. However, to ensure you’re getting the most benefit, and staying safe, we recommend that you:
          <ul>
            <li>Monitor your lab results regularly, including LDL Cholesterol and ApoB, to track your progress</li>
            <li>Check in with your healthcare provider if you have questions, health changes, or are unsure about your results</li>
            <li>Report any side effects or concerns early, especially if your cholesterol levels aren’t improving as expected</li>
          </ul>
        </p>

        <p>If you’d prefer more structured medical support or help interpreting your labs, our <a href="/heart">Heart Health Program</a> offers 1-on-1 physician guidance, regular lab tracking, and a personalized care plan.</p>
      </>)
    },  
    {
      key: "2",
      title: "Possible Side Effects",
      content: (<>
        <p><strong>Common and Usually Mild</strong></p>
      <p>While most people tolerate rosuvastatin 5 mg without issues, some may experience mild side effects such as:
      <ul>
        <li>Muscle soreness or stiffness, particularly in large muscle groups</li>
        <li>Headache, lightheadedness, or general fatigue</li>
        <li>Digestive changes such as constipation, gas, or bloating</li>
        <li>Mild sleep disturbances, usually in the early adjustment period</li>
      </ul>
      </p>

      <p>These effects are usually temporary and fade as your body adjusts. Staying hydrated and maintaining light activity may help minimize early symptoms.</p>

      <p><strong>Rare but Serious</strong></p>

      <p>Although rare, especially at 5 mg, serious side effects can occur, especially in those with pre-existing conditions or drug interactions. These include:
      <ul>
        <li>Rhabdomyolysis, a serious muscle breakdown that can affect kidney function. Look out for muscle pain, profound weakness, or dark urine.</li>
        <li>Liver injury, which may present as yellowing of the skin or eyes, nausea, or right-sided abdominal pain.</li>
        <li>Allergic reactions, including facial swelling, rash, or trouble breathing.</li>
      </ul>
      </p>

      <p>If any of these occur, stop the medication immediately and seek medical attention.</p>
      </>)
    },
    {
      key: "3",
      title: "Insulin Resistance & Metabolic Effects",
      content: (<>
        <p>There is some evidence that statins, including rosuvastatin, can slightly increase insulin resistance or blood glucose levels over time. This effect is more common in:</p>
        <ul>
          <li>Individuals with prediabetes, metabolic syndrome, or obesity</li>
          <li>Those with a family history of type 2 diabetes</li>
          <li>People on higher doses or long-term statin use</li>
        </ul>
        <p>Although this risk is low at the 5 mg dose, we recommend checking fasting glucose or HbA1c as part of your initial lab work if you fall into one of these groups.</p>
      </>)
    },
    {
      key: "4",
      title: "Liver & Muscle Monitoring",
      content: (<>
        <p>Rosuvastatin is metabolized by the liver and may affect liver enzymes or muscle tissue in rare cases. To monitor safely:</p>
        <ul>
          <li>Checking liver function tests (ALT, AST, bilirubin) before starting</li>
          <li>Following up with blood work every 6–12 months if using long-term</li>
          <li>Stay alert for symptoms like muscle pain or unusual fatigue, especially if you’re on other medications that raise statin-related risks</li>
        </ul>
        <p>If you have a history of liver disease, frequent alcohol use, or kidney problems, you may benefit from additional monitoring or enrolling in our <a href="/heart">Heart Health Program</a> for closer physician oversight.</p>
      </>)
    },
    {
      key: "5",
      title: "Who Should Be Cautious",
      content: (<>
        <p>While rosuvastatin 5 mg is safe for most people, it may not be suitable if you have certain health risks. Please speak with a healthcare provider before starting if you:</p>
        <ul>
          <li>Have known liver disease or persistently elevated liver enzymes</li>
          <li>Have chronic kidney disease or a history of muscle disorders</li>
          <li>Are pregnant, trying to conceive, or breastfeeding</li>
          <li>Are over age 70 with multiple health conditions</li>
          <li>Take medications that affect liver or muscle (e.g., certain antibiotics, antivirals, or antifungals)</li>
          <li>Consume alcohol in high amounts or regularly binge drink</li>
        </ul>
        <p>If any of these apply, rosuvastatin may not be appropriate without closer physician oversight. Consider joining our <a href="/heart">Heart Health Program</a> to receive tailored guidance and safe long-term care.</p>
      </>)
    },
    {
      key: "6",
      title: "Medication & Supplement Interactions",
      content: (<>
        <p>Rosuvastatin can interact with medications and supplements in ways that increase the risk of side effects:</p>
        <ul>
          <li>Other statins, such as simvastatin or atorvastatin</li>
          <li>Fibrates (e.g., fenofibrate, gemfibrozil), often used for triglyceride lowering</li>
          <li>High-dose niacin (vitamin B3) or red yeast rice, which have cholesterol-lowering effects</li>
          <li>Certain antivirals or antibiotics, such as ritonavir, cyclosporine, or clarithromycin</li>
          <li>Antacids, which can reduce absorption if taken too close together</li>
        </ul>
        <p>Please disclose all medications and supplements during your intake process so we can safely evaluate your prescription.</p>
      </>)
    },
    {
      key: "7",
      title: "Pregnancy & Breastfeeding",
      content: (<>
        <p>Do not take rosuvastatin if you are pregnant, breastfeeding, or planning to become pregnant. Cholesterol is essential for fetal development, and statins may interfere with this process. If pregnancy occurs while taking rosuvastatin, stop immediately and consult your healthcare provider.</p>
      </>)
    },
    {
      key: "8",
      title: "When to Stop and Seek Medical Advice",
      content: (<>
        <p>Discontinue rosuvastatin and seek medical care if you experience:</p>
        <ul>
          <li>Severe or unexplained muscle pain, weakness, or tenderness (especially if paired with fever or dark urine)</li>
          <li>Yellowing of your skin or eyes, persistent fatigue, or unexplained nausea</li>
          <li>Facial or throat swelling, rash, or breathing difficulty</li>
          <li>New confusion, memory issues, or difficulty concentrating</li>
        </ul>
      </>)
    },
    {
      key: "9",
      title: "About the Heart Health Program",
      content: (<>
        <p>Our <a href="/heart">Heart Health Program</a> is our comprehensive, physician-led care model, which is designed for people who want more support, more data, and more personalized insight.</p>
      
        <p>This program is ideal if you:
        <ul>
          <li>Have existing conditions like insulin resistance, fatty liver, or elevated cardiovascular risk</li>
          <li>Want a doctor to help interpret your labs and guide treatment decisions</li>
          <li>Are new to rosuvastatin and prefer ongoing check-ins</li>
          <li>Have experienced side effects from statins in the past and want export oversight</li>
        </ul>
        </p>
        <p>What’s Included:
        <ul>
          <li>Comprehensive lab testing, including LDL-C, ApoB, glucose, liver function, and more</li>
          <li>1-on-1 physician consultations for treatment planning, lab interpretation, and progress tracking</li>
          <li>Personalized plan to lower ApoB and improve long-term heart health</li>
          <li>Ongoing monitoring to help optimize your regimen and manage side effects</li>
        </ul>
        </p>
        <p>Whether you’re just getting started or managing multiple health priorities, the Heart Health Program gives you the medical partnership you need to move forward with confidence.
        </p>
        <p><Button type="primary" size="large" onClick={() => window.location.href = '/heart'}>Learn More</Button></p>
        </>)
    }
]

export const Rosuvastatin = () => {
  const [faqs, setFaqs] = useState([]);
  const [tabPosition, setTabPosition] = useState("left");
  const navigate = useNavigate();
  const { currentUser } = useContext(UserContext)
  useEffect(() => {
    const fetchFaqs = async () => {
      const filter = {
        code: { 
          $in: [
            FaqCode.ROSUVASTATIN_WHY_TAKE, 
            FaqCode.ROSUVASTATIN_LOW_DOSE, 
            FaqCode.ROSUVASTATIN_SIDE_EFFECTS, 
            FaqCode.ROSUVASTATIN_HOW_TO_KNOW, 
            FaqCode.ROSUVASTATIN_APO_B, 
            FaqCode.ROSUVASTATIN_HOW_LONG,
            FaqCode.ROSUVASTATIN_EVERY_OTHER_DAY,
            FaqCode.ROSUVASTATIN_PHYSICIAN,
            FaqCode.ROSUVASTATIN_INSURANCE,
            FaqCode.ROSUVASTATIN_OTHER_MEDICATION
          ]   
        }
      }
      const response = await listFaqs({filter})
      setFaqs(response)
    }

    fetchFaqs()
  }, [])

  useEffect(() => {
    document.title = "Rosuvastatin (5 mg) - Low-Dose Statin for Long-Term Heart Health"
  }, [])

  useEffect(() => {
    PosthogHelper.track(currentUser, `[treatment] rosuvastatin view landing`, true)
  }, [currentUser])

  useEffect(() => {
    function handleResize() {
      setTabPosition(window.innerWidth < 768 ? "top" : "left");
    }
    handleResize(); // set on mount
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);


  return (
    <div className="treatment-detail">
      {/* Hero Section */}
      <section className="treatment-hero">
        {/* Only for desktop */}
        <div className="treatment-hero__image treatment-hero__image--desktop">
          <img src="/img/treatments/low-dose-statin-on-table.webp" alt="Rosuvastatin Bottle" />
        </div>
        <div className="treatment-hero__info">
          <h1>Rosuvastatin (5mg) <span className="treatment-hero__caption">: Low-Dose Statin for Long-Term Heart Health</span></h1>
          <div className="treatment-hero__badges">
              <span className="badge">Clinically Backed</span>
              <span className="badge">HSA/FSA Eligible</span>
              <span className="badge">Free Shipping</span>
          </div>

          {/* Only for mobile */}
          <div className="treatment-hero__image treatment-hero__image--mobile">
            <img src="/img/treatments/rosuvastatin-bottle-v3.webp" alt="Rosuvastatin Bottle" />
          </div>
          <p className="treatment-hero__subtitle">
            Rosuvastatin is one of the most effective interventions for lowering ApoB & LDL cholesterol, stabilizing plaque, and reducing long-term cardiovascular risk.
            </p>

          <div className="treatment-hero__price">
            <span>Starting at $15/month</span>
          </div>

          <div className="treatment-hero__features">
    
            <div className="treatment-hero__feature">
              <h4>Powerful Results, Minimal Dose</h4>
              <p>Rosuvastatin delivers powerful ApoB & LDL reduction at a low dose, while also improving endothelial health and reducing inflammation - key drivers of heart and brain aging.</p>
            </div>
            <div className="treatment-hero__feature">
              <h4>Same Day Prescription Approval</h4>
              <p>Your prescription is evaluated and approved by a board-certified lipidologist same day. This ensures this medication is safe for you.</p>
            </div>
            <div className="treatment-hero__feature">
              <h4>Trusted Pharmacy Partners</h4>
              <p>Your medication is filled through licensed U.S. pharmacies that follow strict protocols for quality, consistency, and safety, so every tablet meets the highest standard.</p>
            </div>
          </div>
          <div className="treatment-hero__cta">
            

            <div className="cta-container">
           
            <Button className="cta" type="primary" size="large" onClick={() => navigate(`/flow/${FlowType.ROSUVASTATIN}`)}>Get Started Now</Button>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="treatment-benefits">
        <div className="max-width-container">
        <h2>The Benefits of Low-Dose Rosuvastatin
          <div className="caption">A potent, well-tolerated option for long-term cardiovascular prevention.</div>
        </h2>
        <div className="benefits-grid">
          <div className="benefit-card">
            <h3>Lowers ApoB and LDL Cholesterol</h3>
            <p>
              A daily 5 mg dose of rosuvastatin significantly reduces ApoB and LDL, two of the most predictive markers of heart disease risk. Studies show average reductions of 35-42%, even in people with type 2 diabetes or prior statin intolerance
              <CitationFootnote number={1} citation={citations[0]} />
              <CitationFootnote number={2} citation={citations[1]} />
              <CitationFootnote number={3} citation={citations[2]} />
              <CitationFootnote number={4} citation={citations[3]} />
              <CitationFootnote number={5} citation={citations[4]} />.
            </p>
          </div>

          <div className="benefit-card">
            <h3>Helps Prevent Heart Attack and Stroke</h3>
            <p>Large clinical trials show rosuvastatin at low doses meaningfully lowers the risk of heart attacks, strokes, and cardiovascular death, even in people with no history of cardiovascular disease. Risk reduction ranges from 21-24% over 5-9 years, compared to placebo
              <CitationFootnote number={7} citation={citations[6]} />
              <CitationFootnote number={8} citation={citations[7]} />.
            </p>
          </div>
          <div className="benefit-card">
            <h3>Improves Your Overall Lipid Profile</h3>
            <p>
            In addition to targeting ApoB and LDL cholesterol, rosuvastatin 5 mg also lowers triglycerides by 24-30%, reduces total cholesterol by 27–33%, and modestly raises HDL cholesterol, supporting a healthier long-term lipid balance
              <CitationFootnote number={3} citation={citations[2]} />
              <CitationFootnote number={4} citation={citations[3]} />.
            </p>
          </div>
          <div className="benefit-card">
            <h3>Well Tolerated and Low Risk of Side Effects</h3>
            <p>Rosuvastatin 5 mg is generally well tolerated, with a low risk of muscle or liver-related side effects. It's often used successfully in people who couldn't tolerate other statins. Discontinuation due to side effects is rare
              <CitationFootnote number={1} citation={citations[0]} />
              <CitationFootnote number={2} citation={citations[1]} />
              <CitationFootnote number={3} citation={citations[2]} />
              <CitationFootnote number={4} citation={citations[3]} />.
              </p>
          </div>
 
        </div>
        </div>
      </section>

      {/* Treatment Steps */}
      <section className="treatment-steps">
        <div className="max-width-container">
          <h2>How to Start Rosuvastatin
            <div className="caption">A simple, safe, and data-driven way to begin treatment.</div>
          </h2>
          <TreatmentTimeline timelineItems={timelineItems} />
        </div>
      </section>

      {/* Mechanism Section */}
      <section className="treatment-pathways">
        <div className="max-width-container">
        <h2>How Rosuvastatin Works So Well
          <div className="caption">The pharmacology behind its powerful, targeted effects.</div>
        </h2>
        <div className="pathways-tabs">
        <Tabs
          defaultActiveKey="1"
          tabPosition={tabPosition}
          items={mechanismData.map(tab => ({
            key: tab.key,
            label: tab.label,
            children: tab.content,
          }))}

        />
        </div>
      </div>

      </section>

      {/* Safety Information */}
      <section className="treatment-safety">
      <div className="max-width-container">
        <h2>Safety Information
        </h2>

        <div className="safety-info-intro">
          <p>Rosuvastatin 5 mg is a low-dose prescription statin designed to lower LDL cholesterol and ApoB, a key driver of cardiovascular risk. Although this dose is considered gentle, rosuvastatin still affects cholesterol production, liver metabolism, and on rare occasions blood sugar regulation.</p>
        
          <p>To ensure safe and responsible prescribing, we require recent lab results showing your LDL Cholesterol or ApoB levels before initiating treatment. If you haven’t had this testing done, we’ll guide you through how to get it.</p>
        
          <p>If you want more hands-on care, we offer the <a href="/heart">Heart Health Program</a>, which is a physician-led option that includes comprehensive lab testing, 1-on-1 medical support, and a personalized approach to managing both heart and metabolic health.
          </p>
        </div>

        <Collapse
          accordion
          bordered={false}
          className="safety-collapse"
        >
          {safetyInfo.map(item => (
            <Panel header={item.title} key={item.key}>
              {item.content}
            </Panel>
          ))}
     
        </Collapse>
      </div>
      </section>


      {/* FAQ */}
      {faqs?.length > 0 && 
      <section className="treatment-faq">
        <div className="max-width-container">
          <h2>Frequently Asked Questions</h2>
          <Questions items={faqs} />
        </div>
      </section>}


      {/* Related Articles */}
      {/* https://instalab.com/research/statin */}
      {/* https://instalab.com/research/evaluate-medication */}
      {/* https://instalab.com/research/sterol-test */}

    </div>
  );
}

export default Rosuvastatin;